import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Users, 
    Plus, 
    Pencil, 
    Trash2, 
    UserCheck, 
    UserX,
    Search,
    Shield,
    Briefcase,
    HeadphonesIcon,
    Crown,
    ShieldCheck,
    KeyRound,
    ChevronDown,
    ChevronUp,
    LayoutDashboard,
    Eye,
    EyeOff,
    Settings as SettingsIcon,
    Mail as MailIcon,
    Trash2 as TrashIcon,
    Globe,
    Database,
    ChevronRight
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'admin', label: 'Administrateur', icon: Crown, color: 'bg-purple-100 text-purple-700' },
    { value: 'commercial', label: 'Commercial', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
    { value: 'assistante', label: 'Assistante', icon: HeadphonesIcon, color: 'bg-pink-100 text-pink-700' },
    { value: 'responsable', label: 'Responsable', icon: Shield, color: 'bg-green-100 text-green-700' },
];

const MODULES = [
    { id: 'tableau_de_bord', label: 'Tableau de bord' },
    { id: 'prospects', label: 'Prospects' },
    { id: 'inventaire', label: 'Inventaire' },
    { id: 'devis', label: 'Devis' },
    { id: 'clients', label: 'Clients' },
    { id: 'facturation', label: 'Facturation' },
    { id: 'agenda', label: 'Agenda' },
    { id: 'agenda_demenagement', label: 'Agenda déménagement' },
    { id: 'espace_demenageurs', label: 'Espace déménageurs' },
    { id: 'aot', label: 'AOT' },
    { id: 'tarif_aot', label: 'Tarif AOT' },
    { id: 'monte_meuble', label: 'Monte-meuble' },
    { id: 'garde_meuble', label: 'Garde-meuble' },
    { id: 'carte_groupage', label: 'Carte groupage' },
    { id: 'messagerie', label: 'Messagerie' },
    { id: 'statistiques', label: 'Statistiques' },
    { id: 'corbeille', label: 'Corbeille' },
    // Intentionally no "administration" entry: this tab is admin-only by design
    // (see Layout.js `adminOnly: true` and App.js `ProtectedRoute adminOnly`).
];

const DASHBOARD_BLOCKS = [
    { id: 'bloc_planifier', label: 'Planifier une visite' },
    { id: 'bloc_taches', label: 'Tâches' },
    { id: 'bloc_problemes', label: 'Problèmes' },
    { id: 'bloc_devis_non_envoyes', label: 'Devis non envoyés' },
    { id: 'bloc_clients_relancer', label: 'Clients à relancer' },
    { id: 'bloc_prospects_sans_reponse', label: 'Prospects sans réponse' },
    { id: 'bloc_livraison', label: 'Livraison' },
    { id: 'bloc_statistiques', label: 'Statistiques' },
    { id: 'bloc_recherche', label: 'Ma recherche' },
    { id: 'bloc_journal_appels', label: "Journal d'appel" },
];

const STATS_BLOCKS = [
    { id: 'stats_prospects', label: 'Prospects — Analyse' },
    { id: 'stats_devis', label: "Devis — Taux d'acceptation" },
    { id: 'stats_leaderboard', label: 'Leaderboard commercial' },
    { id: 'stats_ca', label: "Chiffre d'affaires" },
    { id: 'stats_tresorerie', label: 'Trésorerie & impayés' },
    { id: 'stats_activite', label: 'Activité opérationnelle' },
    { id: 'stats_benefice', label: 'Bénéfice' },
];

const DEFAULT_PERMISSIONS = {
    ...MODULES.reduce((acc, m) => {
        acc[m.id] = { lecture: false, ecriture: false, suppression: false };
        return acc;
    }, {}),
    dashboard_blocks: DASHBOARD_BLOCKS.reduce((acc, b) => {
        acc[b.id] = true;
        return acc;
    }, {}),
    stats_blocks: STATS_BLOCKS.reduce((acc, b) => {
        acc[b.id] = true;
        return acc;
    }, {}),
};

export default function Administration() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation } = useUnsavedChanges();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'commercial'
    });
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/users`, {
                withCredentials: true
            });
            setUsers(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setFormError('');
        
        if (!formData.name.trim()) {
            setFormError('Le nom est requis');
            return;
        }
        if (!formData.email.trim()) {
            setFormError('L\'email est requis');
            return;
        }
        if (!formData.password.trim()) {
            setFormError('Le mot de passe est requis');
            return;
        }
        
        setSubmitting(true);

        try {
            const response = await axios.post(`${API_URL}/api/users`, {
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                role: formData.role
            }, {
                withCredentials: true
            });
            toast.success('Utilisateur créé avec succès');
            setHasChanges(false);
            setShowCreateModal(false);
            setFormData({ name: '', email: '', password: '', role: 'commercial' });
            await fetchUsers();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (Array.isArray(detail)) {
                setFormError(detail.map(d => d.msg || d).join(', '));
            } else {
                setFormError(typeof detail === 'string' ? detail : 'Erreur lors de la création');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setFormError('');
        
        if (!formData.name.trim()) {
            setFormError('Le nom est requis');
            return;
        }
        if (!formData.email.trim()) {
            setFormError('L\'email est requis');
            return;
        }
        
        setSubmitting(true);

        try {
            const updatePayload = {
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                role: formData.role
            };
            if (formData.password && formData.password.trim()) {
                updatePayload.password = formData.password;
            }
            await axios.put(`${API_URL}/api/users/${selectedUser.id}`, updatePayload, {
                withCredentials: true
            });
            toast.success('Utilisateur modifié avec succès');
            setHasChanges(false);
            setShowEditModal(false);
            setSelectedUser(null);
            await fetchUsers();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (Array.isArray(detail)) {
                setFormError(detail.map(d => d.msg || d).join(', '));
            } else {
                setFormError(typeof detail === 'string' ? detail : 'Erreur lors de la modification');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedUser) return;
        
        setSubmitting(true);
        try {
            await axios.delete(`${API_URL}/api/users/${selectedUser.id}`, {
                withCredentials: true
            });
            toast.success('Utilisateur supprimé');
            setShowDeleteModal(false);
            setSelectedUser(null);
            await fetchUsers();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (user) => {
        try {
            await axios.put(`${API_URL}/api/users/${user.id}`, {
                is_active: !user.is_active
            }, {
                withCredentials: true
            });
            toast.success(user.is_active ? 'Utilisateur désactivé' : 'Utilisateur activé');
            await fetchUsers();
        } catch (error) {
            toast.error('Erreur lors de la modification');
        }
    };

    const handleSavePermissions = async () => {
        if (!selectedUser) return;
        
        setSubmitting(true);
        try {
            await axios.put(`${API_URL}/api/users/${selectedUser.id}/permissions`, {
                permissions: permissions
            }, {
                withCredentials: true
            });
            toast.success('Permissions enregistrées');
            setShowPermissionsModal(false);
            setSelectedUser(null);
            await fetchUsers();
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement des permissions');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setHasChanges(false);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedUser(null);
        setHasChanges(false);
    };

    const openCreateModal = () => {
        setFormData({ name: '', email: '', password: '', role: 'commercial' });
        setFormError('');
        setHasChanges(false);
        setShowCreateModal(true);
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '',
            role: user.role
        });
        setFormError('');
        setHasChanges(false);
        setShowEditModal(true);
    };

    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const openPermissionsModal = (user) => {
        setSelectedUser(user);
        const saved = user.permissions || {};
        const merged = {
            ...MODULES.reduce((acc, m) => {
                acc[m.id] = {
                    lecture: saved[m.id]?.lecture || false,
                    ecriture: saved[m.id]?.ecriture || false,
                    suppression: saved[m.id]?.suppression || false,
                };
                return acc;
            }, {}),
            dashboard_blocks: DASHBOARD_BLOCKS.reduce((acc, b) => {
                acc[b.id] = saved.dashboard_blocks?.[b.id] !== undefined ? saved.dashboard_blocks[b.id] : true;
                return acc;
            }, {}),
            stats_blocks: STATS_BLOCKS.reduce((acc, b) => {
                acc[b.id] = saved.stats_blocks?.[b.id] !== undefined ? saved.stats_blocks[b.id] : true;
                return acc;
            }, {}),
        };
        setPermissions(merged);
        setShowPermissionsModal(true);
    };

    const togglePermission = (moduleId, permType) => {
        setPermissions(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                [permType]: !prev[moduleId]?.[permType]
            }
        }));
    };

    const toggleDashboardBlock = (blockId) => {
        setPermissions(prev => ({
            ...prev,
            dashboard_blocks: {
                ...prev.dashboard_blocks,
                [blockId]: !prev.dashboard_blocks?.[blockId]
            }
        }));
    };

    const toggleStatsBlock = (blockId) => {
        setPermissions(prev => ({
            ...prev,
            stats_blocks: {
                ...prev.stats_blocks,
                [blockId]: !prev.stats_blocks?.[blockId]
            }
        }));
    };

    const [showDashboardBlocks, setShowDashboardBlocks] = useState(false);
    const [showStatsBlocks, setShowStatsBlocks] = useState(false);

    const getRoleBadge = (role) => {
        const roleConfig = ROLES.find(r => r.value === role);
        if (!roleConfig) return <Badge variant="secondary">{role}</Badge>;
        const Icon = roleConfig.icon;
        return (
            <Badge className={`${roleConfig.color} gap-1`}>
                <Icon className="w-3 h-3" />
                {roleConfig.label}
            </Badge>
        );
    };

    const filteredUsers = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            
            <div className="space-y-6 animate-fadeIn">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 
                            className="text-2xl sm:text-3xl font-bold text-[#1B365D]"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                            data-testid="admin-title"
                        >
                            Administration
                        </h1>
                        <p className="text-gray-500 mt-1">
                            Gestion des utilisateurs et des rôles
                        </p>
                    </div>
                    <Button 
                        onClick={openCreateModal}
                        className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                        data-testid="create-user-button"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel utilisateur
                    </Button>
                </div>

                {/* Settings shortcut card */}
                <button
                    type="button"
                    onClick={() => navigate('/administration/parametres')}
                    className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#C9A962]/40 transition-all p-5 flex items-center gap-4 group"
                    data-testid="admin-settings-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1B365D] to-[#2A4A7A] flex items-center justify-center shadow-sm shrink-0">
                        <SettingsIcon className="w-6 h-6 text-[#C9A962]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Paramètres</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Coordonnées entreprise, tarifs par formule, fichiers partagés</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1B365D] transition-colors shrink-0" />
                </button>

                {/* Messagerie shortcut card */}
                <button
                    type="button"
                    onClick={() => navigate('/messagerie')}
                    className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all p-5 flex items-center gap-4 group"
                    data-testid="admin-messagerie-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
                        <MailIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Messagerie</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Emails reçus et envoyés depuis le site / les devis / les visites</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1B365D] transition-colors shrink-0" />
                </button>

                {/* Corbeille shortcut card */}
                <button
                    type="button"
                    onClick={() => navigate('/corbeille')}
                    className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-red-300 transition-all p-5 flex items-center gap-4 group"
                    data-testid="admin-corbeille-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm shrink-0">
                        <TrashIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Corbeille</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Éléments supprimés (restaurables pendant 30 jours)</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1B365D] transition-colors shrink-0" />
                </button>

                {/* Logs formulaire site shortcut card */}
                <button
                    type="button"
                    onClick={() => navigate('/administration/logs-formulaire-site')}
                    className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all p-5 flex items-center gap-4 group"
                    data-testid="admin-public-form-logs-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm shrink-0">
                        <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Logs formulaire site</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Toutes les soumissions reçues depuis luxymove-demenagement.fr (créées, refusées, dédupliquées)</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1B365D] transition-colors shrink-0" />
                </button>

                {/* Sauvegarde / Restauration shortcut card */}
                <button
                    type="button"
                    onClick={() => navigate('/administration/sauvegarde')}
                    className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all p-5 flex items-center gap-4 group"
                    data-testid="admin-sauvegarde-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                        <Database className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Sauvegarde / Restauration</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Exporter toutes les données avant un déploiement, ou restaurer après</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#1B365D] transition-colors shrink-0" />
                </button>

                {/* Users Card */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                                <Users className="w-5 h-5 text-[#C9A962]" />
                                Liste des utilisateurs
                            </CardTitle>
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-users"
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-4 border-[#1B365D] border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">Aucun utilisateur trouvé</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nom</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rôle</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredUsers.map((user) => (
                                            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                                <TableCell className="font-medium">{user.name}</TableCell>
                                                <TableCell className="text-gray-500">{user.email}</TableCell>
                                                <TableCell>{getRoleBadge(user.role)}</TableCell>
                                                <TableCell>
                                                    {user.is_active ? (
                                                        <Badge className="bg-green-100 text-green-700 gap-1">
                                                            <UserCheck className="w-3 h-3" />
                                                            Actif
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-gray-100 text-gray-600 gap-1">
                                                            <UserX className="w-3 h-3" />
                                                            Inactif
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {/* Activer/Désactiver - masqué pour l'admin principal */}
                                                        {!user.is_root_admin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleToggleActive(user)}
                                                                title={user.is_active ? 'Désactiver' : 'Activer'}
                                                                className="h-9 w-9 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                                                data-testid={`toggle-user-${user.id}`}
                                                            >
                                                                <ShieldCheck className="w-5 h-5" />
                                                            </Button>
                                                        )}
                                                        {/* Permissions */}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => openPermissionsModal(user)}
                                                            title="Permissions"
                                                            className="h-9 w-9 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                                            data-testid={`permissions-user-${user.id}`}
                                                        >
                                                            <KeyRound className="w-5 h-5" />
                                                        </Button>
                                                        {/* Modifier */}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => openEditModal(user)}
                                                            title="Modifier"
                                                            className="h-9 w-9 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                                            data-testid={`edit-user-${user.id}`}
                                                        >
                                                            <Pencil className="w-5 h-5" />
                                                        </Button>
                                                        {/* Supprimer - masqué pour l'admin principal */}
                                                        {!user.is_root_admin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => openDeleteModal(user)}
                                                                title="Supprimer"
                                                                className="h-9 w-9 text-gray-500 hover:text-red-600 hover:bg-gray-100"
                                                                data-testid={`delete-user-${user.id}`}
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Roles Info Card */}
                <Card className="shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#C9A962]" />
                            Rôles disponibles
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {ROLES.map((role) => {
                                const Icon = role.icon;
                                return (
                                    <div 
                                        key={role.value}
                                        className="p-4 rounded-xl border border-gray-200 hover:border-[#C9A962] transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${role.color}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-[#1B365D]">{role.label}</p>
                                                <p className="text-xs text-gray-500">
                                                    {role.value === 'admin' && 'Accès total'}
                                                    {role.value === 'commercial' && 'Clients & devis'}
                                                    {role.value === 'assistante' && 'Administratif'}
                                                    {role.value === 'responsable' && 'Supervision'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={(open) => {
                if (!open) { confirmNavigation(closeCreateModal); } 
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">Créer un utilisateur</DialogTitle>
                        <DialogDescription>
                            Remplissez les informations pour créer un nouvel utilisateur.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate}>
                        <div className="space-y-4 py-4">
                            {formError && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                                    {formError}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="name">Nom complet</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                    placeholder="Jean Dupont"
                                    data-testid="create-user-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleFormChange('email', e.target.value)}
                                    placeholder="jean@luxymove.com"
                                    data-testid="create-user-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Mot de passe</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleFormChange('password', e.target.value)}
                                    placeholder="••••••••"
                                    data-testid="create-user-password"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="role">Rôle</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => handleFormChange('role', value)}
                                >
                                    <SelectTrigger data-testid="create-user-role">
                                        <SelectValue placeholder="Sélectionner un rôle" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => confirmNavigation(closeCreateModal)}
                            >
                                Annuler
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={submitting}
                                className="bg-[#1B365D] hover:bg-[#2A4A7A]"
                                data-testid="create-user-submit"
                            >
                                {submitting ? 'Création...' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={(open) => {
                if (!open) { confirmNavigation(closeEditModal); }
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">Modifier l'utilisateur</DialogTitle>
                        <DialogDescription>
                            Modifiez les informations de l'utilisateur.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit}>
                        <div className="space-y-4 py-4">
                            {formError && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                                    {formError}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="edit-name">Nom complet</Label>
                                <Input
                                    id="edit-name"
                                    value={formData.name}
                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                    data-testid="edit-user-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-email">Email</Label>
                                <Input
                                    id="edit-email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleFormChange('email', e.target.value)}
                                    data-testid="edit-user-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-password">Nouveau mot de passe</Label>
                                <Input
                                    id="edit-password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleFormChange('password', e.target.value)}
                                    placeholder="Laisser vide pour ne pas changer"
                                    data-testid="edit-user-password"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-role">Rôle</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => handleFormChange('role', value)}
                                >
                                    <SelectTrigger data-testid="edit-user-role">
                                        <SelectValue placeholder="Sélectionner un rôle" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => confirmNavigation(closeEditModal)}
                            >
                                Annuler
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={submitting}
                                className="bg-[#1B365D] hover:bg-[#2A4A7A]"
                                data-testid="edit-user-submit"
                            >
                                {submitting ? 'Modification...' : 'Modifier'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Permissions Modal */}
            <Dialog open={showPermissionsModal} onOpenChange={(open) => {
                setShowPermissionsModal(open);
                if (!open) { setSelectedUser(null); setShowDashboardBlocks(false); setShowStatsBlocks(false); }
            }}>
                <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">
                            Permissions de {selectedUser?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Gérez les droits d'accès pour chaque module
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {/* Modules Table */}
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-[#1B365D]">Module</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-[#1B365D]">Lecture</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-[#1B365D]">Écriture</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-[#1B365D]">Suppression</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-[#1B365D]">Autre</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {MODULES.map((module) => (
                                        <tr key={module.id} className="hover:bg-gray-50" data-testid={`perm-row-${module.id}`}>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    {module.label}
                                                    {module.id === 'tableau_de_bord' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowDashboardBlocks(!showDashboardBlocks)}
                                                            className="p-0.5 rounded hover:bg-[#1B365D]/10 transition-colors"
                                                            data-testid="toggle-dashboard-blocks"
                                                        >
                                                            {showDashboardBlocks ? (
                                                                <ChevronUp className="w-4 h-4 text-[#C9A962]" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4 text-[#C9A962]" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {module.id === 'statistiques' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowStatsBlocks(!showStatsBlocks)}
                                                            className="p-0.5 rounded hover:bg-[#1B365D]/10 transition-colors"
                                                            data-testid="toggle-stats-blocks"
                                                        >
                                                            {showStatsBlocks ? (
                                                                <ChevronUp className="w-4 h-4 text-[#C9A962]" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4 text-[#C9A962]" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex justify-center">
                                                    <Switch
                                                        checked={permissions[module.id]?.lecture || false}
                                                        onCheckedChange={() => togglePermission(module.id, 'lecture')}
                                                        className="data-[state=checked]:bg-[#1B365D]"
                                                        data-testid={`perm-${module.id}-lecture`}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex justify-center">
                                                    <Switch
                                                        checked={permissions[module.id]?.ecriture || false}
                                                        onCheckedChange={() => togglePermission(module.id, 'ecriture')}
                                                        className="data-[state=checked]:bg-[#1B365D]"
                                                        data-testid={`perm-${module.id}-ecriture`}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex justify-center">
                                                    <Switch
                                                        checked={permissions[module.id]?.suppression || false}
                                                        onCheckedChange={() => togglePermission(module.id, 'suppression')}
                                                        className="data-[state=checked]:bg-[#1B365D]"
                                                        data-testid={`perm-${module.id}-suppression`}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {module.id === 'devis' ? (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Switch
                                                            checked={permissions.devis?.change_statut || false}
                                                            onCheckedChange={() => togglePermission('devis', 'change_statut')}
                                                            className="data-[state=checked]:bg-[#C9A962]"
                                                            data-testid="perm-devis-change-statut"
                                                        />
                                                        <span className="text-[10px] text-gray-500">Changer statut</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Dashboard Blocks - Expandable */}
                        {showDashboardBlocks && (
                            <div className="border rounded-lg overflow-hidden animate-fadeIn" data-testid="dashboard-blocks-section">
                                <div className="bg-[#1B365D]/5 px-4 py-2.5 flex items-center gap-2 border-b">
                                    <LayoutDashboard className="w-4 h-4 text-[#C9A962]" />
                                    <span className="text-sm font-semibold text-[#1B365D]">Blocs du Tableau de bord</span>
                                    <span className="text-xs text-gray-400 ml-auto">Visible / Caché</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {DASHBOARD_BLOCKS.map((block) => {
                                        const isVisible = permissions.dashboard_blocks?.[block.id] !== false;
                                        return (
                                            <div
                                                key={block.id}
                                                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                                                data-testid={`bloc-row-${block.id}`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {isVisible ? (
                                                        <Eye className="w-4 h-4 text-[#1B365D]" />
                                                    ) : (
                                                        <EyeOff className="w-4 h-4 text-gray-300" />
                                                    )}
                                                    <span className={`text-sm ${isVisible ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                                                        {block.label}
                                                    </span>
                                                </div>
                                                <Switch
                                                    checked={isVisible}
                                                    onCheckedChange={() => toggleDashboardBlock(block.id)}
                                                    className="data-[state=checked]:bg-[#1B365D]"
                                                    data-testid={`bloc-${block.id}-toggle`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Stats Blocks - Expandable */}
                        {showStatsBlocks && (
                            <div className="border rounded-lg overflow-hidden animate-fadeIn" data-testid="stats-blocks-section">
                                <div className="bg-[#0EA5E9]/5 px-4 py-2.5 flex items-center gap-2 border-b">
                                    <LayoutDashboard className="w-4 h-4 text-[#0EA5E9]" />
                                    <span className="text-sm font-semibold text-[#1B365D]">Sous-blocs Statistiques</span>
                                    <span className="text-xs text-gray-400 ml-auto">Accessible / Masqué</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {STATS_BLOCKS.map((block) => {
                                        const isVisible = permissions.stats_blocks?.[block.id] !== false;
                                        return (
                                            <div
                                                key={block.id}
                                                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                                                data-testid={`stats-bloc-row-${block.id}`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {isVisible ? (
                                                        <Eye className="w-4 h-4 text-[#1B365D]" />
                                                    ) : (
                                                        <EyeOff className="w-4 h-4 text-gray-300" />
                                                    )}
                                                    <span className={`text-sm ${isVisible ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                                                        {block.label}
                                                    </span>
                                                </div>
                                                <Switch
                                                    checked={isVisible}
                                                    onCheckedChange={() => toggleStatsBlock(block.id)}
                                                    className="data-[state=checked]:bg-[#0EA5E9]"
                                                    data-testid={`stats-bloc-${block.id}-toggle`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setShowPermissionsModal(false)}
                        >
                            Annuler
                        </Button>
                        <Button 
                            type="button" 
                            onClick={handleSavePermissions}
                            disabled={submitting}
                            className="bg-[#1B365D] hover:bg-[#2A4A7A] gap-2"
                            data-testid="save-permissions"
                        >
                            <ShieldCheck className="w-4 h-4" />
                            {submitting ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={showDeleteModal} onOpenChange={(open) => {
                setShowDeleteModal(open);
                if (!open) setSelectedUser(null);
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Supprimer l'utilisateur</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer <strong>{selectedUser?.name}</strong> ? Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Annuler
                        </Button>
                        <Button 
                            type="button" 
                            onClick={handleDelete}
                            disabled={submitting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="delete-user-confirm"
                        >
                            {submitting ? 'Suppression...' : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <UnsavedDialog />
        </Layout>
    );
}
